"use client"

import { useCallback } from 'react'
import { useAuth, type AuthUser } from './use-auth'
import { Permission, Role, UserAuthData } from '@/lib/auth/permissions.types'
import {
  checkPermission,
  checkAllPermissions,
  hasRole as checkRole,
  getUserPermissions,
  getActiveTenantId,
  isImpersonating,
  canImpersonate
} from '@/lib/auth/permissions.utils'

/**
 * Hook for checking user permissions in React components
 * Provides convenient methods to check permissions and roles
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { can, hasRole, isSuperUser } = usePermissions()
 *
 *   if (!can('users.create')) {
 *     return <div>Access denied</div>
 *   }
 *
 *   return <button>Create User</button>
 * }
 * ```
 */
export function usePermissions(authenticatedUser?: AuthUser) {
  const { user: authUser } = useAuth()
  const user = authenticatedUser ?? authUser

  /**
   * Check if user has specific permission(s)
   * Returns true if user has ANY of the specified permissions
   * @param permission - Single permission or array of permissions
   *
   * Memoised on `user` so consumers can safely pass it to `useCallback`
   * deps without triggering an infinite re-render loop. Identity changes
   * only when the auth user changes (login, logout, JWT refresh with
   * new permissions, impersonation switch).
   */
  const can = useCallback(
    (permission: Permission | Permission[]): boolean => {
      if (!user) return false
      return checkPermission(user as UserAuthData, permission)
    },
    [user],
  )

  /**
   * Check if user has ALL specified permissions
   * Unlike 'can' which checks for ANY, this requires ALL
   * @param permissions - Array of permissions (all must match)
   */
  const canAll = useCallback(
    (permissions: Permission[]): boolean => {
      if (!user) return false
      return checkAllPermissions(user as UserAuthData, permissions)
    },
    [user],
  )

  /**
   * Check if user has specific role(s)
   * Returns true if user has ANY of the specified roles
   * @param role - Single role or array of roles
   */
  const hasRole = useCallback(
    (role: Role | Role[]): boolean => {
      if (!user) return false
      return checkRole(user as UserAuthData, role)
    },
    [user],
  )

  /**
   * Get all user permissions (role + additional)
   */
  const permissions = user ? getUserPermissions(user as UserAuthData) : []

  /**
   * Get active tenant ID (considering impersonation)
   */
  const activeTenantId = user ? getActiveTenantId(user as UserAuthData) : null

  /**
   * Check if user is currently impersonating another tenant
   */
  const impersonating = user ? isImpersonating(user as UserAuthData) : false

  /**
   * Check if user can impersonate (switch tenants)
   */
  const canSwitchTenant = user ? canImpersonate(user as UserAuthData) : false

  // Convenience role checks.
  // integrator is admin-level (folded into isAdmin/isManager); isSuperUser stays
  // strict so platform-only surfaces remain hidden from integrator.
  const isSuperUser = hasRole('super_user')
  const isIntegrator = hasRole('integrator')
  const isAdmin = hasRole(['super_user', 'integrator', 'admin'])
  const isManager = hasRole(['super_user', 'integrator', 'admin', 'manager'])
  const isViewer = hasRole('viewer')
  // Cross-tenant staff: may see platform-wide data (provider health, other
  // tenants' AI logs). Mirrors isPlatformStaff() on the server.
  const isPlatformStaff = hasRole(['super_user', 'integrator'])

  // Whether the user's HOME tenant is the platform tenant (UI hint from the JWT
  // claim). Platform-only surfaces gate on `isSuperUser || (isPlatformTenant && can(...))`.
  const isPlatformTenant = !!(user as UserAuthData | null)?.is_platform_tenant

  return {
    // Permission checks
    can,
    canAll,

    // Role checks
    hasRole,
    isSuperUser,
    isIntegrator,
    isPlatformStaff,
    isAdmin,
    isManager,
    isViewer,
    isPlatformTenant,

    // Permission list
    permissions,

    // Tenant info
    activeTenantId,
    impersonating,
    canSwitchTenant
  }
}
